<template>
  <section class="login-form">
    <ValidationObserver
      ref="loginObserver"
      tag="div"
      class="form-container"
      v-slot="{ handleSubmit, pristine, invalid }"
    >
      <h1 class="title" v-t="'appLoginForm.message.loginToIField'" />
      <div class="login-error">
        <p v-if="errorMessage" class="error-message">
          {{ errorMessage }}
        </p>
      </div>
      <b-form v-on:submit.prevent="handleSubmit($_onSubmit)">
        <ValidationProvider
          v-for="form in forms"
          v-bind:key="form.key"
          tag="div"
          v-bind:name="form.id.form"
          v-bind:rules="form.rules"
          class="form-item"
          v-slot="validationContext"
        >
          <b-form-input
            v-bind:id="form.id.form"
            v-bind:type="form.type"
            v-model="value[form.key]"
            v-bind:state="$_toState(validationContext)"
            v-bind:placeholder="form.placeholder"
            v-bind:tabindex="form.tabindex"
            v-bind:autocomplete="form.autocomplete"
            class="form"
          />
          <div class="form-related">
            <button
              class="button-clear"
              v-bind:class="{ visible: form.clearable }"
              v-on:mousedown.prevent
              v-on:click="value[form.key] = null"
              type="button"
            >
              <icon-clear class="icon-clear" />
            </button>
            <div class="icon-validation" />
          </div>

          <b-form-invalid-feedback v-bind:id="form.id.feedback">
            <p class="invalid-message">
              {{ form.invalidMessage }}
            </p>
          </b-form-invalid-feedback>

          <b-form-checkbox
            v-if="form.key === FORM_KEY.PASSWORD"
            class="show-password-checkbox"
            v-model="showPassword"
          >
            <span class="label" v-t="'appLoginForm.action.showPassword'" />
          </b-form-checkbox>
        </ValidationProvider>
        <b-button
          type="submit"
          variant="success"
          v-bind:disabled="pristine || invalid"
          class="btn-submit"
          tabindex="4"
          v-t="'appLoginForm.action.login'"
        />
      </b-form>
    </ValidationObserver>
    <div class="forgot-password">
      <a
        v-on:click="$_onClickForgotPassword"
        v-on:keyup.enter="$_onClickForgotPassword"
        v-t="'appLoginForm.message.forgotPassword'"
        tabindex="5"
      />
    </div>
  </section>
</template>

<script>
import IconClear from '@/assets/ifield/text-clear.svg?component'

/**
 * フォーム値が空の場合はtrue
 * @param {string} フォーム値
 * @returns {boolean}
 */
const isBlank = (value) => value == null || value === ''

/**
 * フォームキー定数
 */
const FORM_KEY = {
  LOGIN_CODE: 'loginCode',
  PASSWORD: 'password',
}

export default {
  name: 'AppLoginForm',
  components: {
    'icon-clear': IconClear,
  },
  props: {
    /**
     * ログインコード
     */
    loginCode: {
      type: String,
      default: null,
    },
    /**
     * エラーメッセージ
     */
    errorMessage: {
      type: String,
      default: '',
    },
  },
  data: function () {
    return {
      value: {
        loginCode: this.loginCode,
        password: null,
      },
      rules: {
        loginCode: { required: true, min: 1, max: 50, regex: /^[!-~]*$/ },
        password: { required: true, min: 1, max: 50, regex: /^[!-~]*$/ },
      },
      placeholder: {
        loginCode: this.$t('appLoginForm.loginCode'),
        password: this.$t('appLoginForm.password'),
      },
      invalidMessage: {
        loginCode: this.$t('appLoginForm.message.invalid.loginCode'),
        password: this.$t('appLoginForm.message.invalid.password'),
      },
      showPassword: false,
    }
  },
  computed: {
    /**
     * フォームのキー
     * @type {Object}
     */
    FORM_KEY: function () {
      return FORM_KEY
    },
    /**
     * フォーム定義
     * @type {Array.<Object>}
     */
    forms: function () {
      const keys = [FORM_KEY.LOGIN_CODE, FORM_KEY.PASSWORD]
      return keys.map((key, index) => ({
        key,
        id: {
          form: `input-${key}`,
          feedback: `input-${key}-feedback`,
        },
        type:
          key === FORM_KEY.LOGIN_CODE || this.showPassword
            ? 'text'
            : 'password',
        rules: this.rules[key],
        placeholder: this.placeholder[key],
        invalidMessage: this.invalidMessage[key],
        tabindex: index + 1,
        clearable: !isBlank(this.value[key]),
        autocomplete:
          key === FORM_KEY.PASSWORD ? 'current-password' : 'username',
      }))
    },
  },
  watch: {
    loginCode: function (value) {
      this.value.loginCode = value
    },
  },
  methods: {
    /**
     * バリデーションフラグを状態に変換
     * @param {boolean} dirty ダーティーフラグ
     * @param {boolean} changed 変更フラグ
     * @param {boolean} valid validフラグ
     * @returns {boolean} 状態
     */
    $_toState: function ({ dirty, changed, valid }) {
      return dirty || changed ? valid : null
    },

    /**
     * ログインボタン押下時処理
     */
    $_onSubmit: function () {
      /**
       * ログインイベント
       * @param {Object} value ログイン情報(loginCode, password)
       */
      this.$emit('login', this.value)
    },

    /**
     * パスワード忘れリンククリック時処理
     */
    $_onClickForgotPassword: function () {
      /**
       * パスワード忘れたイベント
       */
      this.$emit('forgot-password')
    },
  },
}
</script>

<style lang="scss" scoped>
.login-form {
  width: 27.5rem;
  background-color: #ffffff;
  box-shadow: 0 4px 40px rgba(0, 0, 0, 0.05);
  border: 1px solid #aaaaaa;
  padding: 48px;
}

.form-container {
  margin-bottom: 24px;
}

.title {
  font-size: $font-size-xlarge;
  text-align: center;
  font-weight: $font-weight-bold;
  line-height: 1;
  margin: 0 0 8px 0;
}

.login-error {
  margin: 0;
  min-height: 2rem;
  height: auto;

  .error-message {
    margin: 0;
    text-align: center;
    color: #dc3545;
    font-size: $font-size-middle;
    line-height: 1.25;
    word-break: break-all;
    overflow-wrap: break-word;
  }
}

// ユーザの入力する部分に関する設定
.form-item {
  position: relative;
  margin: 0;
  height: 5rem;

  &:has(.show-password-checkbox) {
    // フォーム高 + チェックボックス高
    height: calc(5rem + 24px);
    .form {
      margin-bottom: 12px;
      &.is-invalid {
        margin-bottom: 0px;
      }
    }
  }

  .form {
    position: relative;
    top: 0;
    left: 0;
    width: 100%;
    height: 2.5rem;
    padding-right: 68px !important;
    border: 1px solid #aaaaaa;
    border-radius: 4px;
    background-image: none !important;
  }
}

// クリアボタンとバリデーションアイコンを表示する部分の設定
// オートコンプリート時におけるバリデーションアイコン非表示対策で設定
.form-related {
  position: absolute;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: auto;
  height: 2.5rem;
  right: 0.5rem;
  top: 0;

  .form:focus-within + & {
    .button-clear.visible {
      display: block;
    }
  }
}

// クリアボタンに関する設定
.button-clear {
  appearance: none;
  outline: none;
  border: none;
  background: none;

  display: none;
  width: 1.625rem;
  height: 1.625rem;
  padding: 0;
  top: 0;

  z-index: 40;

  &:hover {
    background: #eee;
    border-radius: 50%;
  }

  .icon-clear {
    display: block;
    position: relative;
    margin: auto;
    fill: $entity-color-black;
  }
}

// バリデーションアイコンに関する設定
.icon-validation {
  display: block;
  top: 0;
  bottom: 0;
  right: 0;
  width: 0;
  height: 2.5rem;
  background-origin: padding-box;
  background-image: none !important;
  background-repeat: no-repeat;
  background-size: 1.125rem;
  background-position: center;

  z-index: 20;

  // バリデーションが有効になった場合に、独自でバリデーションアイコンを設定する。
  .is-valid + .form-related > & {
    width: 1.5rem;
    height: 100%;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 8 8'%3e%3cpath fill='%2328a745' d='M2.3 6.73L.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e") !important;
  }

  .is-invalid + .form-related > & {
    width: 1.5rem;
    height: 100%;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e") !important;
  }
}

.invalid-message {
  text-align: left;
  margin: 0;
}

.show-password-checkbox {
  text-align: left;
  margin-bottom: auto;
  height: 24px;
  .label {
    font-size: $font-size-small;
    width: max-content;
    position: absolute;
    top: 4px;
    left: -4px;
    line-height: 1rem;
    user-select: none;
  }
}

// ログインボタンに関する設定
.btn-submit {
  width: 100%;
  height: 2.5rem;
  color: #fff;
  font-weight: $font-weight-bold;
  background-color: #1ebe67;
  border-color: #1ebe67;

  &:hover,
  &:focus {
    color: #fff;
    background-color: #12a554;
    border-color: #1ebe67;
  }

  // 入力フォームに何も無いときは、ボタンをhoverしても色を灰色にする
  &.disabled {
    pointer-events: none;
    background-color: #6c757d;
    border-color: #6c757d;
  }
}

// パスワードを忘れたボタンの設定
.forgot-password {
  color: #1ca570;
  font-size: $font-size-middle;
  display: flex;
  justify-content: center;
  cursor: pointer;
  line-height: 1;

  &:hover {
    text-decoration: underline;
  }
}
</style>
